import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon } from "lucide-react";
import { z } from "zod";
import { updateUserClientLinkUserClientLinksUserIdClientIdPatch } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { EmailListInput } from "@/components/admin/UserTable/PricePreference/EmailListInput";
import { PriceUpdatePreferenceForm } from "@/components/admin/UserTable/PricePreference/PriceUpdatePreferenceForm";
import {
  electricitySchema,
  emissionsSchema,
  gasSchema,
  sparkSpreadSchema,
} from "@/components/admin/UserTable/PricePreference/schema";
import { PERMISSIONS, permissionsSchema } from "@/lib/utils";

export const userClientLinkSchema = z.object({
  id: z.number(),
  client_id: z.number(),
  client_code: z.string(),
  email: z.string().email(),
  permissions: permissionsSchema.nullable(),
  estimated_price_update_preferences: z
    .object({
      emissions: emissionsSchema,
      gas: gasSchema,
      electricity: electricitySchema,
      sparkspread: sparkSpreadSchema,
    })
    .nullable(),
  cc_recipients: z.array(z.string().email()).default([]),
  bcc_recipients: z.array(z.string().email()).default([]),
});

export type UserClientLinkFormValues = z.infer<typeof userClientLinkSchema>;

interface EditUserClientLinkFormProps {
  defaultValues: UserClientLinkFormValues;
}

export function EditUserClientLinkForm({ defaultValues }: EditUserClientLinkFormProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<z.input<typeof userClientLinkSchema>, unknown, UserClientLinkFormValues>({
    resolver: zodResolver(userClientLinkSchema),
    defaultValues: defaultValues,
  });

  const { mutateAsync: updateUserClientLink, isPending: isUpdatingUserClientLink } = useMutation({
    mutationFn: (data: UserClientLinkFormValues) =>
      updateUserClientLinkUserClientLinksUserIdClientIdPatch({
        userId: defaultValues.id,
        clientId: defaultValues.client_id,
        requestBody: {
          permissions: data.permissions ?? [],
          cc_recipients: [...new Set(data.cc_recipients)],
          bcc_recipients: [...new Set(data.bcc_recipients)],
          estimated_price_update_preferences: data.estimated_price_update_preferences,
        },
      }),
  });

  async function handleSubmit(data: UserClientLinkFormValues) {
    await updateUserClientLink(data);
    toast({
      title: "Operation successful",
      description: "The user client link has been updated",
    });
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["user-client-links"] });
    window.location.reload();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <PencilIcon className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[425px] md:max-w-[1200px]">
        <DialogHeader>
          <DialogTitle>Edit User Client Link</DialogTitle>
          <DialogDescription>Edit the user client link details and permissions.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Code</FormLabel>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="permissions"
              render={({ field }) => (
                <FormItem>
                  <div className="mb-4">
                    <FormLabel>Permissions</FormLabel>
                    <FormDescription>Select the permissions for this user-client link.</FormDescription>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {PERMISSIONS.map((permission) => (
                      <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(permission.id)}
                            onCheckedChange={(checked) => {
                              const currentPermissions = field.value || [];
                              return checked
                                ? field.onChange([...currentPermissions, permission.id])
                                : field.onChange(currentPermissions?.filter((value) => value !== permission.id));
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">{permission.label}</FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <EmailListInput
              name="cc_recipients"
              label="CC Recipients"
              description="Emails that receive price-update CC."
            />
            <EmailListInput
              name="bcc_recipients"
              label="BCC Recipients"
              description="Emails that receive price-update BCC."
            />
            <PriceUpdatePreferenceForm index={0} key={useId()} />

            <DialogFooter>
              <Button type="submit" disabled={isUpdatingUserClientLink}>
                {isUpdatingUserClientLink ? "Updating..." : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
